// Shared URL/document classification for user-entered links across the platform.
// Distinguishes Tagdada-hosted resources (uploaded docs served from our backend,
// which should be displayed/downloaded) from external links (which must open the
// real site in a new tab). External links missing a scheme get https:// prepended
// so the browser does not resolve them relative to the Tagdada origin.

const BACKEND = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

// Returns { type: 'empty' | 'internal' | 'external', href, display }
export function classifyLink(raw) {
  const value = (raw || '').trim();
  if (!value) return { type: 'empty', href: '', display: '' };

  // Contact protocols pass through untouched.
  if (/^(mailto:|tel:)/i.test(value)) {
    return { type: 'external', href: value, display: value };
  }

  // Relative Tagdada API resource → resolve against our backend.
  if (value.startsWith('/api/')) {
    return { type: 'internal', href: `${BACKEND}${value}`, display: value };
  }

  // Absolute URL pointing at our own backend → internal resource.
  if (BACKEND && value.startsWith(BACKEND)) {
    return { type: 'internal', href: value, display: value.replace(/^https?:\/\//, '') };
  }

  // Already has a web scheme → external as-is.
  if (/^https?:\/\//i.test(value)) {
    return { type: 'external', href: value, display: value.replace(/^https?:\/\//, '') };
  }

  // No scheme → external link missing protocol → always https.
  const cleaned = value.replace(/^\/+/, '');
  return { type: 'external', href: `https://${cleaned}`, display: cleaned };
}

// Normalizes a user-entered URL for storage. Leaves internal/relative paths and
// existing schemes intact; prepends https:// to bare external links.
export function normalizeUrl(raw) {
  const value = (raw || '').trim();
  if (!value) return '';
  if (value.startsWith('/api/') || /^(https?:\/\/|mailto:|tel:)/i.test(value)) return value;
  return `https://${value.replace(/^\/+/, '')}`;
}
